import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { DashboardInfoWidgetViewModel } from 'src/app/modules/settings/viewModels/dashboardInfoWidgetViewModel';
import { EventsService } from 'src/app/modules/shared/services/events-service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { EventViewModel } from 'src/app/modules/shared/viewModels/events/eventViewModel';
import moment from 'moment';
import { LightFilterUpdateModel } from 'src/app/modules/shared/models/filter/LightFilterUpdateModel';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { Constants } from 'src/app/modules/shared/models/constants';
import { Subscription } from 'rxjs';
import { OperationTypes } from 'src/app/modules/shared/enums/operationTypes';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { IntegrationTypes } from 'src/app/modules/shared/enums/integrationTypes';

@Component({
    selector: 'app-event-add-edit-modal',
    templateUrl: './event-add-edit-modal.component.html',
    styleUrls: ['./event-add-edit-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EventAddEditModalComponent implements OnInit, OnDestroy {
  @Output() eventEmitted: EventEmitter<EventViewModel> = new EventEmitter<EventViewModel>();

  public headerText: string;
  public eventId: number;
  public event: EventViewModel;
  public lightUpdateModel: LightFilterUpdateModel;
  public filterTypes = FilterTypes;
  public dashboardInfoWidgetEditMode: boolean = true;
  public loading: boolean = false;
  public allEventTypeFilters: FilterViewModel[] = [];
  public triggerErrors: boolean = false;
  public eventForm: UntypedFormGroup;
  public localisedEvent: string;
  public localisedEventType: string;
  public readonly T = T;
  private isTouched: boolean = false;
  public isMobile: boolean = false;
  protected subscriptions = new Subscription();
  protected localisedItem: string;
  protected integrationTypes = IntegrationTypes;

  @HostListener('document:keydown.enter') onEnterHandler() {
    if (this.modalIsValid()) this.handleConfirm();
  }

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly eventsService: EventsService,
    private readonly authenticationService: AuthenticationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly localisationService: LocalisationService,
    private readonly fb: UntypedFormBuilder,
    private readonly translateService: TranslateService,
    private readonly wtValidators: WtValidators,
    private readonly confirmationService: ConfirmationService,
    private readonly alertService: AlertService,
  ) {}

  ngOnInit() {
    this.loading = true;
    this.isMobile = window.innerWidth < Constants.mobileWidth;
    this.localisedItem = this.localisationService.localiseObjectType(ObjectTypes.Event);

    this.initLocalisations();
    this.initForm();

    if (this.eventId) {
      this.getEventDetails();
    } else {
      this.initNewEvent();
    }

    this.allEventTypeFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Event_Type);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initLocalisations(): void {
    this.localisedEvent = this.localisationService.localiseObjectType(ObjectTypes.Event);
    this.localisedEventType = this.translateService.instant(T.defaultLocalizations.event_type.one);
  }

  private setFormControls(): void {
    this.eventForm.controls.title.setValue(this.event.title);
  }

  private getEventDetails() {
    this.eventsService.getEventDetails(this.eventId).subscribe((event) => {
      this.lightUpdateModel = new LightFilterUpdateModel();
      this.event = event;
      this.setFormControls();
      this.initHeaderText();
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  private initNewEvent(): void {
    this.event = new EventViewModel();
    this.event.dashboardWidgetInfo = new DashboardInfoWidgetViewModel(this.translateService);
    this.event.accountId = this.authenticationService.getCurrentAccount().id;
    this.initHeaderText();
    this.event.startDate = moment().set("hour", 7).set("minute", 0).toISOString();
    this.event.endDate = moment().add(1, 'd').set("hour", 7).set("minute", 0).toISOString();
    this.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  private initForm(): void {
    this.eventForm = this.fb.group({
      title: [
        '',
        {
          validators: [
            Validators.required,
            this.wtValidators.textInput(3, 80, WtValidators.RESTRICTED_CHARS.DEFAULT),
          ],
        },
      ],
    });
  }

  private initHeaderText(): void {
    this.headerText = this.eventId === 0
      ? this.translateService.instant(T.common.add_new_item, { item: this.localisedEvent })
      : this.event.title;
  }

  public onConfirm(): void {
    if (this.modalIsValid()) {
      this.handleConfirm();
    }
  }

  private handleConfirm(): void {
    this.loading = true;
    this.event.title = this.eventForm.controls.title.value;

    if (this.isMobile) {
      this.createEventOnMobile();
      return;
    }

    this.eventEmitted.next(this.event);
    this.closeModal();
  }

  private closeModal(): void {
    this.bsModalRef.hide();
  }

  public confirmClose(): void {
    this.isTouched = this.eventForm.dirty || this.isTouched;
    if (this.isTouched) {
      this.confirmationService.confirmThis(
        this.translateService.instant(T.common.are_sure_you_want_to_leave_page),
        () => {
          this.closeModal();
        },
        () => {
          //handle cancel
        },
        this.translateService.instant(T.common.confirm),
        false,
        this.translateService.instant(T.common.stay),
        this.translateService.instant(T.common.leave),
        'danger'
      );
    } else {
      this.closeModal();
    }
  }

  public handleFilterChange(filters: FilterViewModel[]): void {
    this.event.filters = filters;
    this.isTouched = true;
  }

  public onStartDateChanged(dateAsIsoString: string) {
    this.isTouched = true;
    this.event.startDate = dateAsIsoString;

    if (this.event.endDate.valueOf() < dateAsIsoString.valueOf()) {
      this.event.endDate = dateAsIsoString;
    }
  }

  public onEndDateChanged(dateAsIsoString: string): void {
    this.isTouched = true;
    this.event.endDate = dateAsIsoString;

    if (this.event.startDate.valueOf() > dateAsIsoString.valueOf()) {
      this.event.startDate = dateAsIsoString;
    }
  }

  public onWidgetInfoUpdated(widgetInfo: DashboardInfoWidgetViewModel): void {
    this.event.dashboardWidgetInfo = widgetInfo;
  }

  public toggleInfoWidgetEditMode(): void {
    this.dashboardInfoWidgetEditMode = !this.dashboardInfoWidgetEditMode;
  }

  public modalIsValid(): boolean {
    return this.eventForm.valid;
  }

  public localiseString(s: string): string {
    return this.localisationService.localise(s);
  }

  public onStartTimeChanged(dateAsObj: string): void {
    this.isTouched = true;
    this.event.startDate = dateAsObj;
  }

  public onEndTimeChanged(dateAsObj: string): void {
    this.isTouched = true;
    this.event.endDate = dateAsObj;
  }

  private createEventOnMobile(): void {
    this.subscriptions.add(
      this.eventsService.addEvent(this.event).subscribe((eventRes) => {
        void this.alertService.success(this.translateService.instant(T.common.item_was_added_successfully, { item: this.localisedItem }));
        this.allowedFiltersService.refreshFilters(
          OperationTypes.Create,
          [{ id: eventRes.id, title: eventRes.title, relatedObjectId: 0 }],
          FilterTypes.Event
        );
        this.changeDetectorRef.detectChanges();
        this.closeModal();
      })
    );
  }
}
